import { Box, Stack } from "@mui/material";
import { Image, PictureAsPdf } from "@mui/icons-material";
import { getGridContextMenuList } from "../../../_helpers/getGridContextMenuList";
import { LOCAL_STORAGE_GRID_KEYS } from "../../../_constants";
import { formatDate, formatDateForFilter, nl2br } from "../../../_helpers";
import { HoverImage, LabelColumnV2, MultiLabelColumnV2 } from "../../../components/DataGridV2/DataColumnsV2";
import { URLS } from "../../../apis/urls";
import OfflineFileWrapper from "../../../components/OfflineFileWrapper";


export const getSurveyRepairRequestColumns = ({
    t,
    keyPrefix,
    i18n,
    apiRef,
    actionColumns,
    organizations,
    inspection,
    locations,
    statusOptions
}) => {
    const getOptions = getGridContextMenuList({
        filterKey: LOCAL_STORAGE_GRID_KEYS.SURVEY_REPAIR_REQUESTS_COLUMN_FILTER[keyPrefix],
        sortKey: LOCAL_STORAGE_GRID_KEYS.SURVEY_REPAIR_REQUESTS_COLUMN_SORT[keyPrefix],
        i18n,
        t,
        apiRef,
        mode: "client",
        getValueForFilter: (rowData, extraData) => {
            let value = ""
            switch (extraData.fieldName) {
                case "date":
                    value = formatDateForFilter(rowData.date)
                    break;
                default:
                    value = rowData[extraData.fieldName]
                    break;
            }
            return value
        },
        getValueForCopy: (rowData, extraData) => {
            let value = ""
            switch (extraData.fieldName) {
                case "targetCompletionDate":
                    value = rowData?.targetCompletionDate ? formatDate(new Date(rowData?.targetCompletionDate)) : ""
                    break;
                case "date":
                    value = formatDate(new Date(rowData.date))
                    break;
                default:
                    value = rowData?.[extraData?.fieldName]
                    break;
            }
            return value
        }
    })
    const inspectionDetailColumns = inspection
        ? [
            {
                download: true,
                print: true,
                field: "location",
                headerName: t("general.location"),
                flex: 1,
                align: "start",
                minWidth: 108,
                editable: false,
                disableRowSelectionOnClick: true,
                type: "singleSelect",
                valueOptions: locations,
                renderCell: (params) => {
                    const request = params.row;
                    if (!request) return "";
                    return (
                        <LabelColumnV2
                            label={request.location}
                            sx={{ minWidth: "108px" }}
                            cellSubMenuOptions={{ options: getOptions({ field: "location", isQuickFilterDisplay: Boolean(request.location) }), extraData: { fieldName: "location", type: 'singleSelect' } }}
                            request={request}
                        />
                    );
                },
            },
        ]
        : [];
    return [
        {
            field: "attachments",
            headerName: t("general.image"),
            editable: false,
            disableColumnMenu: true,
            filterable: false,
            sortable: false,
            download: false,
            print: true,
            align: "center",
            flex: 1,
            minWidth: 45,
            maxWidth: 50,
            forceApplyWidth: true,

            renderCell: (params) => {
                const request = params.row;
                if (!request) return null;
                let imageURL = "";
                let attachmentId;
                const fileType = request?.attachments?.[0]?.type?.toLowerCase(); //Note:- Offline mode - if the pdf/image file is present then only type is exist in the attachements object

                if (request?.sync === 0) {
                    if (request?.attachments?.length > 0 && fileType !== "application/pdf") {
                        imageURL = window.URL.createObjectURL(new Blob([request.attachments[0]]));
                    }
                } else if (request?.sync !== 0) {
                    if (request?.attachments?.length > 0) {
                        const attachment = request.attachments[0];
                        if (attachment?.attachmentId) {
                            attachmentId = attachment.attachmentId;
                            imageURL = `${URLS.GET_ATTACHMENT_THUMBNAIL}${attachmentId}`;
                        } else if (attachment?.attachmentUrl) {
                            imageURL = attachment.attachmentUrl;
                        }
                    } else if (request?.pdfAttachments?.length > 0) {
                        const pdfAttachment = request.pdfAttachments[0];
                        attachmentId = pdfAttachment.attachmentId;
                        imageURL = `${URLS.GET_ATTACHMENT_THUMBNAIL}${attachmentId}`;
                    }
                }

                return fileType === "application/pdf" ? (
                    <Stack direction="row" sx={{ width: "100%", justifyContent: "center" }}>
                        <PictureAsPdf sx={{ color: "primary.main" }} />
                    </Stack>
                ) : (
                    <OfflineFileWrapper id={attachmentId} url={imageURL}>
                        {(url) => (
                            <HoverImage
                                defaultSize={20}
                                imageId={attachmentId}
                                index={params.rowIndex || params.id}
                            />
                        )}
                    </OfflineFileWrapper>
                );
            },

            renderHeader: () => (
                <Box sx={{ textAlign: "center" }}>
                    <Image />
                </Box>
            ),
        },
        {
            download: true,
            print: true,
            field: "number",
            headerName: t("general.number"),
            flex: 1,
            minWidth: 108,
            renderCell: (params) => {
                const request = params.row;
                if (!request) return "";
                return (
                    <LabelColumnV2
                        label={request.number || t("general.draft")}
                        tooltipLabel={request.number || t("general.draft")}
                        cellSubMenuOptions={{ options: getOptions({ field: "number", isQuickFilterDisplay: Boolean(request.number) }), extraData: { fieldName: "number" } }}
                        request={request}
                    />
                );
            },
        },
        ...inspectionDetailColumns,
        {
            field: "date",
            download: true,
            type: "date",
            valueGetter: (params) => {
                if (!params) return "";
                return new Date(params);
            },
            headerName: t("general.date"),
            flex: 1,
            minWidth: 90,
            renderCell: (params) => {
                const rowData = params?.row;
                if (!rowData) return "";
                return (
                    <LabelColumnV2 label={rowData?.date ? formatDate(new Date(rowData?.date)) : ""}
                        cellSubMenuOptions={{ options: getOptions({ field: "date", isQuickFilterDisplay: rowData?.date }), extraData: { fieldName: "date", type: "date" } }}
                        request={rowData}
                    />
                )
            },
        },
        {
            download: true,
            print: true,
            field: "desc",
            headerName: t("general.description"),
            flex: 1,
            align: "start",
            minWidth: 100,
            renderCell: (params) => {
                const rowData = params.row;
                if (!rowData) return "";
                return (
                    <LabelColumnV2
                        tooltipLabel={nl2br(rowData.detailDesc)}
                        label={rowData.desc}
                        cellSubMenuOptions={{ options: getOptions({ field: "desc", isQuickFilterDisplay: Boolean(rowData.detailDesc) }), extraData: { fieldName: "desc" } }}
                        request={rowData}
                        sx={{ minWidth: "265px" }}
                    />
                );
            },
        },
        {
            download: true,
            print: true,
            field: "textResolvers",
            headerName: t("general.resolvers"),
            minWidth: 142,
            editable: false,
            filterable:false,
            flex: 1,
            align: "start",
            sortable: false,
            renderCell: (params) => {
                const rowData = params.row;
                if (!rowData) return "";
                return (
                    <MultiLabelColumnV2
                        data={ rowData.resolvers ?  rowData?.resolvers.map((x) => ({name: x.name})) : rowData.textResolvers.map((name) => ({name: name})) }
                        cellSubMenuOptions={{ options: getOptions({ sortable: false,filterable : false, field: "textResolvers", isQuickFilterDisplay: false}), extraData: { fieldName: "textResolvers",} }}
                        request={rowData}
                        sx={{ minWidth: "142px" }}
                    />
                );
            },
        },
        {
            download: true,
            print: true,
            field: "targetCompletionDate",
            headerName: t("repairRequest.targetCompletionDate.shortLabel"),
            flex: 1,
            minWidth: 90,
            type: "date",
            valueGetter: (params) => {
                if (!params) return "";
                return new Date(params);
            },
            renderCell: (params) => {
                const rowData = params.row;
                if (!rowData) return "";
                return (
                    <LabelColumnV2
                        date={rowData.targetCompletionDate}
                        cellSubMenuOptions={{ options: getOptions({ field: "targetCompletionDate", isQuickFilterDisplay: rowData?.targetCompletionDate }), extraData: { fieldName: "targetCompletionDate", type: "date" } }}
                        request={rowData}
                    />
                );
            },
        },
        {
            download: true,
            print: true,
            field: "status",
            headerName: t("general.status"),
            flex: 1,
            type:'singleSelect',
            valueOptions:statusOptions,
            align: "start",
            minWidth: 90,
            renderCell: (params) => {
                const rowData = params.row;
                if (!rowData) return "";
                return (
                    <LabelColumnV2
                        label={rowData.status}
                        cellSubMenuOptions={{ options: getOptions({ field: "status", isQuickFilterDisplay: Boolean(rowData.status) }), extraData: { fieldName: "status" , type:'singleSelect'} }}
                        request={rowData}
                    />
                );
            },
        },
        ...actionColumns,
    ];
}

